import uuidv4 from 'uuid/v4';
import { IMenuItem } from './common/ESDataTable';
import {
    ActionItemAttachmentFileDto,
    ActionItemBestPracticeDto,
    ActionStatusDto,
    BehaviorDto,
    ChevronDto,
    IActionItemDto,
    IActionStatusDto,
    IAffinityClusterDto,
    IAffinityClusterObservationStatsDto,
    IAssessmentDto,
    IAssessmentProcessDto,
    IAttachmentFileDto,
    IBehaviorDto,
    IBestPracticeDto,
    IBranchDto,
    IBranchKpiDto,
    IChevronDto,
    IDashboardObservationStatsDto,
    IdentifierEntityDto,
    IIdentifierEntityDto,
    IKpiDto,
    IKpiTypeDto,
    INoteDto,
    IObservationDto,
    IObservationStatsDto,
    IObservationTypeDto,
    IOrganizationDto,
    IRegionDto,
    ISimpleEntityDto,
    IStepDto,
    IUserDto,
    KpiTypeDto,
    NoteDto,
    ObservationTypeDto,
    RegionDto,
    SimpleEntityDto,
    StepDto,
} from './generated_client';

const Defaults = (isNew = false) => ({
    id: uuidv4(),
    isMenuOpen: false,
    isModified: false,
    isNew,
    menuItems: [] as IMenuItem[],
});

export type IUser = typeof User & IUserDto;
export let DefaultUser = (isNew = false) =>
    Object.assign(
        {
            address: '',
            city: '',
            displayRoles: '',
            email: '',
            firstName: '',
            id: '',
            isActive: true,
            jobTitle: '',
            lastName: '',
            name: '',
            organization: {} as SimpleEntityDto | undefined,
            organizationId: '',
            phoneNumber: '',
            profileImageContent: '',
            profileImageMimeType: '',
            profileImageName: '',
            profileImageUrl: '',
            roles: [] as string[],
            state: '',
            userName: '',
            zipCode: '',
        },
        Defaults(isNew)
    );
const User = DefaultUser();

export type IIdentifierEntity = typeof IdentifierEntity & IIdentifierEntityDto;
export let DefaultIdentifierEntity = (isNew = false) =>
    Object.assign(
        {
            id: '',
            name: '',
        },
        Defaults(isNew)
    );
const IdentifierEntity = DefaultIdentifierEntity();

export type ISimpleEntity = typeof SimpleEntity & ISimpleEntityDto;
export let DefaultSimpleEntity = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
        },
        Defaults(isNew)
    );
const SimpleEntity = DefaultSimpleEntity();

export type IOrganization = typeof Organization & IOrganizationDto;
export let DefaultOrganization = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            description: '',
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
        },
        Defaults(isNew)
    );
const Organization = DefaultOrganization();

export type IAffinityCluster = typeof AffinityCluster & IAffinityClusterDto;
export let DefaultAffinityCluster = (isNew = false) =>
    Object.assign(
        {
            assessment: {} as IdentifierEntityDto,
            createdOn: new Date(0, 0),
            deletedOn: new Date(0, 0),
            id: '',
            isActive: false,
            modifiedOn: new Date(0, 0),
            name: '',
        },
        Defaults(isNew)
    );
const AffinityCluster = DefaultAffinityCluster();

export type IAssessment = typeof Assessment & IAssessmentDto;
export let DefaultAssessment = (isNew = false) =>
    Object.assign(
        {
            affinityClusters: [] as IAffinityClusterDto[],
            comparisonBranches: [] as IdentifierEntityDto[],
            createdOn: new Date(),
            dueDate: undefined as Date | undefined,
            history: '',
            id: '',
            improvementGoals: '',
            improvementStrategy: '',
            isActive: true,
            kpis: [] as IdentifierEntityDto[],
            modifiedOn: new Date(),
            name: '',
            observers: [] as IdentifierEntityDto[],
            targetBranch: new IdentifierEntityDto(),
            visitEndedOn: undefined as Date | undefined,
            visitStartedOn: new Date(),
        },
        Defaults(isNew)
    );
const Assessment = DefaultAssessment();

export type IAssessmentProcess = typeof AssessmentProcess & IAssessmentProcessDto;
export let DefaultAssessmentProcess = (isNew = false) =>
    Object.assign(
        {
            chevrons: [] as ChevronDto[],
            createdOn: new Date(),
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
        },
        Defaults(isNew)
    );
const AssessmentProcess = DefaultAssessmentProcess();

export type IChevron = typeof Chevron & IChevronDto;
export let DefaultChevron = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            displayOrder: 0,
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
            steps: [] as StepDto[],
        },
        Defaults(isNew)
    );
const Chevron = DefaultChevron();

export type IStep = typeof Step & IStepDto;
export let DefaultStep = (isNew = false) =>
    Object.assign(
        {
            behaviors: [] as BehaviorDto[],
            chevronId: '',
            createdOn: new Date(),
            displayOrder: 0,
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
            skipped: false,
        },
        Defaults(isNew)
    );
const Step = DefaultStep();

export type IBehavior = typeof Behavior & IBehaviorDto;
export let DefaultBehavior = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            displayOrder: 0,
            hasOccurred: false,
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
            stepId: '',
        },
        Defaults(isNew)
    );
const Behavior = DefaultBehavior();

export type IAttachmentFile = typeof AttachmentFile & IAttachmentFileDto;
export let DefaultAttachmentFile = (isNew = false) =>
    Object.assign(
        {
            content: '',
            createdOn: new Date(),
            id: '',
            isActive: true,
            mimeType: '',
            modifiedOn: new Date(),
            name: '',
            url: '',
        },
        Defaults(isNew)
    );
const AttachmentFile = DefaultAttachmentFile();

export type IObservation = typeof Observation & IObservationDto;
export let DefaultObservation = (isNew = false) =>
    Object.assign(
        {
            affinityClusterId: '',
            assessmentId: '',
            behaviorId: '',
            createdOn: new Date(),
            hasFilesIcon: undefined as JSX.Element | undefined,
            icon: undefined as JSX.Element | undefined,
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
            observationDescription: '',
            observationType: {} as ObservationTypeDto,
            observer: {} as IdentifierEntityDto,
        },
        Defaults(isNew)
    );
const Observation = DefaultObservation();

export type IObservationType = typeof ObservationType & IObservationTypeDto;
export let DefaultObservationType = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
        },
        Defaults(isNew)
    );
const ObservationType = DefaultObservationType();

export type IBestPractice = typeof BestPractice & IBestPracticeDto;
export let DefaultBestPractice = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
        },
        Defaults(isNew)
    );
const BestPractice = DefaultBestPractice();

export type IRegion = typeof Region & IRegionDto;
export let DefaultRegion = (isNew = false) =>
    Object.assign(
        {
            champions: [] as IdentifierEntityDto[],
            createdOn: new Date(),
            deletedOn: new Date(),
            displayChampions: '',
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
        },
        Defaults(isNew)
    );
const Region = DefaultRegion();

export type IBranch = typeof Branch & IBranchDto;
export let DefaultBranch = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            deletedOn: new Date(),
            distributor: '',
            distributorCode: '',
            generalManager: '',
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
            region: {} as RegionDto,
        },
        Defaults(isNew)
    );
const Branch = DefaultBranch();

export type IBranchKpi = typeof BranchKpi & IBranchKpiDto;
export let DefaultBranchKpi = (isNew = false) =>
    Object.assign(
        {
            branch: {} as IdentifierEntityDto,
            createdOn: new Date(),
            deletedOn: new Date(),
            id: '',
            isActive: true,
            kpi: {} as IdentifierEntityDto,
            modifiedOn: new Date(),
            name: '',
            value: 0,
        },
        Defaults(isNew)
    );
const BranchKpi = DefaultBranchKpi();

export type IKpi = typeof Kpi & IKpiDto;
export let DefaultKpi = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            description: '',
            id: '',
            isActive: true,
            kpiType: {} as KpiTypeDto,
            lowerLimit: 0,
            modifiedOn: new Date(),
            name: '',
            shortName: '',
            upperLimit: 0,
        },
        Defaults(isNew)
    );
const Kpi = DefaultKpi();

export type IKpiType = typeof KpiType & IKpiTypeDto;
export let DefaultKpiType = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            deletedOn: new Date(),
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
        },
        Defaults(isNew)
    );
const KpiType = DefaultKpiType();

export type IActionItem = typeof ActionItem & IActionItemDto;
export let DefaultActionItem = (isNew = false) =>
    Object.assign(
        {
            acceptanceCriteria: '',
            actionItemAttachmentFiles: [] as ActionItemAttachmentFileDto[],
            assessment: {} as IdentifierEntityDto,
            assigneeEmail: '',
            assigneeName: '',
            bestPractices: [] as ActionItemBestPracticeDto[],
            createdOn: new Date(),
            description: '',
            dueDate: new Date(),
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
            notes: [] as NoteDto[],
            status: {} as ActionStatusDto,
        },
        Defaults(isNew)
    );
const ActionItem = DefaultActionItem();

export type IActionStatus = typeof ActionStatus & IActionStatusDto;
export let DefaultActionStatus = (isNew = false) =>
    Object.assign(
        {
            createdOn: new Date(),
            deletedOn: new Date(),
            id: '',
            isActive: true,
            modifiedOn: new Date(),
            name: '',
            sortOrder: 0,
        },
        Defaults(isNew)
    );
const ActionStatus = DefaultActionStatus();

export type INote = typeof Note & INoteDto;
export let DefaultNote = (isNew = false) =>
    Object.assign(
        {
            dateCreated: new Date(),
            id: '',
            isActive: true,
            text: '',
        },
        Defaults(isNew)
    );
const Note = DefaultNote();

export type IDashboardObservationStats = typeof DashboardObservationStats &
    IDashboardObservationStatsDto;
export let DefaultDashboardObservationStats = (isNew = false) =>
    Object.assign(
        {
            AffinityClusterObservationStats: [],
            AssessmentId: '',
            ObservationStats: [],
        },
        Defaults(isNew)
    );
const DashboardObservationStats = DefaultDashboardObservationStats();

export type IAffinityClusterObservationStats = typeof AffinityClusterObservationStats &
    IAffinityClusterObservationStatsDto;
export let DefaultAffinityClusterObservationStats = (isNew = false) =>
    Object.assign(
        {
            AffinityClusterName: '',
            ObservationStats: [],
        },
        Defaults(isNew)
    );
const AffinityClusterObservationStats = DefaultAffinityClusterObservationStats();

export type IObservationStats = typeof ObservationStats & IObservationStatsDto;
export let DefaultObservationStats = (isNew = false) =>
    Object.assign(
        {
            NumObservations: [],
            ObservationType: '',
        },
        Defaults(isNew)
    );
const ObservationStats = DefaultObservationStats();



// WEBPACK FOOTER //
// ./src/Definitions.ts